// ─────────────────────────────────────────────
//  importCoverage — what this import understood, and what it needs a person for.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  THE MEASURED PROBLEM. On 2026-08-20 the sheet named 62 clinicians; 40 landed
//  on a board with an id and 22 did not. The importer's only report of that was
//  a list of 22 strings offered for BLIND CREATION — so the night's answer to
//  "we could not identify this person" was to manufacture one. Twenty-three
//  duplicate colleagues appeared in two days; one real person exists five times.
//
//  THE RULING (product owner, 2026-08-20): **never create — always ask a human.**
//  Which only works if the asking is cheap and lands somewhere a human looks:
//
//    62 named · 40 placed · 22 need you · 0 unreadable
//
//  ⚠️ `named` is PEOPLE-INSTANCES, `unreadable` is CELLS. A person named in two
//  rooms is two chances to be wrong, so they count twice; a cell nobody could
//  read named nobody, so it cannot be counted as a person at all. That is why
//  placed + needsYou === named and `unreadable` sits outside the sum.
//
//  ⚠️ THE QUEUE DISTINGUISHES TWO THINGS THAT LOOK IDENTICAL IN A STRING LIST.
//  Measured on today's sheet: FIVE people have no contact-list record at all (a
//  genuine new hire or locum), and TWO are the same colleague spelled two ways
//  across the department's own two workbooks — `Sarabel Mercado Torres` vs
//  `Sarabel Mercado`, `Krista Sciulla` vs `Krista Scullia` (a transposition).
//  Offering "create this person" for the second kind is exactly how the
//  duplicates were minted. They get different words and different default
//  actions, and the near-miss is NAMED so the reviewer can see the evidence.
//
//  ⚠️ SUGGESTING IS NOT RESOLVING. `resolveIdentity` refuses anything it cannot
//  prove, and that refusal is load-bearing — it is what stops a confident wrong
//  answer putting the wrong clinician in a room with a patient. The near-miss
//  search here is deliberately LOOSER than the ladder, because its output is not
//  an assignment: it is a sentence shown to a human who knows the department.
//  Nothing in this file may write, and nothing downstream may act on a
//  suggestion that a person has not clicked.
//
//  Pure — no Firebase, no React, no network.
// ─────────────────────────────────────────────

import { normalizeName } from "../team-directory/normalizeName";
import { nameParts, surnameOf } from "../../domain/canonicalPerson";

/** What a queue item is asking for. The wording and the default action differ. */
export const REVIEW_KINDS = {
  // A person on file is spelled differently here. Default action: confirm.
  SPELLING: "spelling",
  // Nobody on file resembles this name. Default action: skip; creating is a
  // deliberate second click.
  NEW_PERSON: "newPerson",
  // Two people on file fit and the resolver refused to choose. Default: pick.
  AMBIGUOUS: "ambiguous",
  // The cell itself could not be read — no name to ask about.
  UNREADABLE: "unreadable",
};

// How many near-misses to show. A reviewer scanning twenty items at 05:40 reads
// the first one or two; a list of nine is a list nobody finishes.
const MAX_SUGGESTIONS = 3;

// ── near-miss search ─────────────────────────────────────────────────────────

const letters = (s) => String(s ?? "").toLowerCase().replace(/[^a-z]/g, "");

/**
 * Damerau-Levenshtein distance, capped — the cap is what makes it cheap enough
 * to run every unresolved name against a 163-person roster.
 *
 * ⚠️ TRANSPOSITION IS A SEPARATE OPERATION ON PURPOSE. `Sciulla` → `Scullia` is
 * ONE swapped pair; plain Levenshtein scores it 2 and would rank it level with
 * two unrelated typos. The department's misspellings are overwhelmingly
 * transpositions and doubled letters, so the metric has to see them.
 */
export function editDistance(a, b, cap = 3) {
  const s = letters(a);
  const t = letters(b);
  if (s === t) return 0;
  if (Math.abs(s.length - t.length) > cap) return cap + 1;
  const prevPrev = new Array(t.length + 1).fill(0);
  let prev = Array.from({ length: t.length + 1 }, (_, j) => j);
  let cur = new Array(t.length + 1).fill(0);
  let twoBack = prevPrev;
  for (let i = 1; i <= s.length; i += 1) {
    cur[0] = i;
    let best = cur[0];
    for (let j = 1; j <= t.length; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      let v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && s[i - 1] === t[j - 2] && s[i - 2] === t[j - 1]) {
        v = Math.min(v, twoBack[j - 2] + 1);
      }
      cur[j] = v;
      if (v < best) best = v;
    }
    if (best > cap) return cap + 1;
    twoBack = prev;
    prev = cur;
    cur = new Array(t.length + 1).fill(0);
  }
  return prev[t.length];
}

const tokenSet = (name) => new Set(nameParts(name).map(letters).filter(Boolean));
const isSuperset = (big, small) => small.size > 0 && [...small].every((x) => big.has(x));

/**
 * People on file who might be this person, ranked, with the REASON each is
 * offered. Never an answer — a sentence for a human.
 *
 * The four shapes measured on the live workbooks, in confidence order:
 *   1. one name's tokens contain the other's — `Sarabel Mercado Torres` ⊃
 *      `Sarabel Mercado`. The contact list drops the second surname.
 *   2. same given name, surname within a transposition or two — `Krista
 *      Sciulla` ~ `Krista Scullia`.
 *   3. same surname — the rung `resolveIdentity` already reports as candidates
 *      (two Bennetts), repeated here so a queue item is self-contained.
 *   4. the whole string within two edits.
 *
 * @param {string} rawName
 * @param {Array<{id:string,name:string}>} staff
 * @returns {Array<{staffId:string, name:string, why:string, rank:number}>}
 */
export function suggestCandidates(rawName, staff = []) {
  const raw = String(rawName ?? "").trim();
  if (!raw) return [];
  const rawTokens = tokenSet(raw);
  const rawSurname = surnameOf(raw);
  const rawFirst = letters(nameParts(raw)[0] || "");
  const out = [];

  for (const p of staff) {
    if (!p || !p.id || !p.name) continue;
    const theirTokens = tokenSet(p.name);
    const theirSurname = surnameOf(p.name);
    const theirFirst = letters(nameParts(p.name)[0] || "");
    if (normalizeName(p.name) === normalizeName(raw)) continue; // would have resolved

    if (rawTokens.size >= 2 && theirTokens.size >= 2
      && (isSuperset(rawTokens, theirTokens) || isSuperset(theirTokens, rawTokens))) {
      out.push({ staffId: p.id, name: p.name, why: "same name with an extra or missing surname", rank: 1 });
      continue;
    }
    if (rawFirst && rawFirst === theirFirst && rawSurname && theirSurname) {
      const d = editDistance(rawSurname, theirSurname, 2);
      if (d > 0 && d <= 2) {
        out.push({ staffId: p.id, name: p.name, why: `same first name, surname differs by ${d} letter${d === 1 ? "" : "s"}`, rank: 2 });
        continue;
      }
    }
    if (rawSurname && rawSurname === theirSurname) {
      out.push({ staffId: p.id, name: p.name, why: "same surname", rank: 3 });
      continue;
    }
    const whole = editDistance(raw, p.name, 2);
    if (whole > 0 && whole <= 2) {
      out.push({ staffId: p.id, name: p.name, why: `spelled ${whole} letter${whole === 1 ? "" : "s"} apart`, rank: 4 });
    }
  }

  return out.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name)).slice(0, MAX_SUGGESTIONS);
}

// ── queue items ──────────────────────────────────────────────────────────────

/**
 * One unresolved person → one review item, with the words a reviewer needs.
 *
 * @param {object} u  an entry from toSheetDraftGrid's `unresolved.people`
 * @param {Array} staff  the pool the near-miss search runs against
 * @param {string} facilityId
 */
export function buildPersonItem(u, staff = [], facilityId = "") {
  const resolverCandidates = (u.candidates || []).map((c) => ({
    staffId: c.id, name: c.name, why: "same surname", rank: 3,
  }));
  const suggested = suggestCandidates(u.name, staff);
  // The resolver's own candidates first — they are the ones it looked at and
  // refused to choose between, which is a stronger signal than a string metric.
  const seen = new Set();
  const candidates = [...resolverCandidates, ...suggested].filter((c) => {
    if (!c.staffId || seen.has(c.staffId)) return false;
    seen.add(c.staffId);
    return true;
  }).slice(0, MAX_SUGGESTIONS);

  const kind = u.reason === "ambiguous"
    ? REVIEW_KINDS.AMBIGUOUS
    : candidates.length
      ? REVIEW_KINDS.SPELLING
      : REVIEW_KINDS.NEW_PERSON;

  return {
    id: `${facilityId}|${u.sheetRow ?? "?"}|${u.room || "-"}|${normalizeName(u.name)}`,
    kind,
    facilityId,
    room: u.room || "",
    sheetRow: u.sheetRow ?? null,
    // ⚠️ THE CELL AS THE DEPARTMENT WROTE IT. A reviewer settling an identity is
    // reading the sheet over somebody's shoulder; a cleaned-up name is the one
    // thing that cannot help them.
    verbatim: u.verbatim || u.name,
    name: u.name,
    role: u.role || "seat",
    understood: understoodLine(u),
    why: whyLine(kind, u, candidates),
    candidates,
    defaultAction: kind === REVIEW_KINDS.NEW_PERSON ? "skip" : "confirm",
  };
}

function understoodLine(u) {
  const bits = [`the name "${u.name}"`];
  if (u.shiftCode) bits.push(`leaving at ${u.shiftCode}`);
  if (u.room) bits.push(u.role === "seat" ? `holding ${u.room}` : `alongside in ${u.room}`);
  else bits.push("on the bench, in no room");
  return bits.join(", ");
}

function whyLine(kind, u, candidates) {
  if (kind === REVIEW_KINDS.AMBIGUOUS) {
    return `More than one person on file answers to this name (${candidates.map((c) => c.name).join(", ")}). `
      + "Nobody is assigned until you say which one.";
  }
  if (kind === REVIEW_KINDS.SPELLING) {
    const top = candidates[0];
    return `Nobody on file is spelled exactly this way, but ${top.name} is ${top.why}. `
      + "If that is the same person, confirming teaches the importer this spelling for good.";
  }
  return "Nobody on file resembles this name — a new hire, a locum, or a name the "
    + "contact list has never carried. Skipping leaves the name on the board without a "
    + "profile; adding them is a deliberate act, because a wrong add is a duplicate colleague.";
}

/** A cell the reader could not vouch for → an item with no name to confirm. */
export function buildUnreadableItem(r, facilityId = "") {
  return {
    id: `${facilityId}|${r.sheetRow ?? "?"}|${r.code}|${normalizeName(r.text || "")}`,
    kind: REVIEW_KINDS.UNREADABLE,
    facilityId,
    room: r.room || "",
    sheetRow: r.sheetRow ?? null,
    verbatim: r.text || "",
    name: "",
    role: "",
    understood: r.detail ? String(r.detail) : "nothing — the cell did not read as people",
    why: "The importer refused to guess how many people this cell names, so it put "
      + "nobody in the room rather than inventing somebody. Fix the cell in the sheet and re-import.",
    candidates: [],
    defaultAction: "skip",
  };
}

// Only these parser codes mean "a cell I could not read". Everything else in
// `review` is context (a benched room, a note) and must not inflate the count a
// reviewer is asked to clear.
const UNREADABLE_CODES = new Set([
  "nameNotUnderstood",
  "nameNotOfferedForCreation",
  "orientNotAPair",
  "coProviderNotAPair",
  "coProviderAmbiguous",
]);

export const isUnreadableCode = (code) => UNREADABLE_CODES.has(String(code || ""));

// ── the report ───────────────────────────────────────────────────────────────

/**
 * Roll one facility's draft into the four numbers and its queue.
 *
 * @param {object} f  a `parseGlobalSheet().byFacility[fid]` entry (or the
 *   toSheetDraftGrid result directly — the fields read are the same)
 * @param {Array} staff
 * @param {string} facilityId
 */
export function facilityCoverage(f = {}, staff = [], facilityId = "") {
  const people = f.unresolved?.people || [];
  const placed = Number(f.placed || 0);
  const unreadableItems = (f.review || []).filter((r) => isUnreadableCode(r.code));
  const items = [
    ...people.map((u) => buildPersonItem(u, staff, facilityId)),
    ...unreadableItems.map((r) => buildUnreadableItem(r, facilityId)),
  ];
  return {
    facilityId,
    named: placed + people.length,
    placed,
    needsYou: people.length,
    unreadable: unreadableItems.length,
    unknownDoctorCodes: [...(f.unresolved?.doctorCodes || [])],
    items,
  };
}

/** Add up several facilities into one report — what the reviewer is shown. */
export function sumCoverage(list = []) {
  const parts = list.filter(Boolean);
  return {
    named: parts.reduce((n, c) => n + (c.named || 0), 0),
    placed: parts.reduce((n, c) => n + (c.placed || 0), 0),
    needsYou: parts.reduce((n, c) => n + (c.needsYou || 0), 0),
    unreadable: parts.reduce((n, c) => n + (c.unreadable || 0), 0),
    unknownDoctorCodes: [...new Set(parts.flatMap((c) => c.unknownDoctorCodes || []))],
    items: parts.flatMap((c) => c.items || []),
    byFacility: Object.fromEntries(parts.map((c) => [c.facilityId, {
      named: c.named, placed: c.placed, needsYou: c.needsYou, unreadable: c.unreadable,
    }])),
  };
}

/**
 * The one line. Rendered before anything is applied, logged by the unattended
 * job, and stored on the run record — the same sentence in all three places, so
 * a night nobody watched can be compared with a morning somebody did.
 */
export function coverageLine(cov = {}) {
  const n = (v) => Number(v || 0);
  return `${n(cov.named)} named · ${n(cov.placed)} placed · ${n(cov.needsYou)} need you · `
    + `${n(cov.unreadable)} unreadable`;
}

/** The percentage the redesign is measured on. Guarded against an empty sheet. */
export function placementRate(cov = {}) {
  const named = Number(cov.named || 0);
  if (!named) return 0;
  return Number(cov.placed || 0) / named;
}
